import BuyerSalesExplorer from "./BuyerSalesExplorer";
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { api } from "../lib/api";
import { CHART_COLORS, formatNum, compact } from "../lib/format";
import { downloadCsv } from "../lib/csv";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, Treemap,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend
} from "recharts";
import { AlertCircle, Loader2 } from "lucide-react";

function EmptyState({ msg = "No data" }) {
  return (
    <div className="h-full flex items-center justify-center text-[12.5px]" style={{ color: "var(--c-text-muted)" }}>
      {msg}
    </div>
  );
}

function LoadingState() {
  return (
    <div className="h-full flex items-center justify-center" style={{ color: "var(--c-text-muted)" }}>
      <Loader2 size={18} className="animate-spin" strokeWidth={1.7} />
    </div>
  );
}

function ErrorState({ msg, onRetry }) {
  return (
    <div className="h-full flex flex-col items-center justify-center gap-1.5 text-center px-3" data-testid="widget-error">
      <AlertCircle size={16} strokeWidth={1.7} color="#fa4d56" />
      <div className="text-[12px] leading-tight" style={{ color: "#991b1b" }}>{msg}</div>
      {onRetry && (
        <button className="btn btn-ghost text-[11px] mt-1" onClick={onRetry} data-testid="widget-retry">Retry</button>
      )}
    </div>
  );
}

function Kpi({ data, fmt, title, onDrillDown }) {
  const value = data?.value ?? 0;
  const exact = formatNum(value, fmt || {});
  const content = (
    <>
      <div className="kpi-value" title={exact}>{compact(value)}</div>
      <div className="text-[11px] mt-1 numeric" style={{ color: "var(--c-text-muted)" }}>
        {exact}
      </div>
      {null}
    </>
  );

  if (!onDrillDown) {
    return (
      <div className="h-full flex flex-col justify-center px-1" data-testid={`kpi-${title || "widget"}`}>
        {content}
      </div>
    );
  }

  return (
    <button
      type="button"
      className="h-full w-full flex flex-col justify-center px-1 text-left rounded-md"
      onClick={() => onDrillDown({ label: title || "Total", value })}
      data-testid={`kpi-${title || "widget"}`}
      title="View detailed rows"
      style={{ cursor: "pointer" }}
    >
      {content}
    </button>
  );
}

function FarmAuditSummary({ data, onDrillDown }) {
  const summary = data?.summary || {};
  const metrics = [
    ["Total Farms", summary.total_farms],
    ["BAP", summary.bap_farms],
    ["ASC", summary.asc_farms],
    ["Non-Certified", summary.non_certified_farms],
  ];

  const secondary = [
    ["Non-BAP", summary.non_bap_farms],
    ["Unmarked", summary.unmarked_farms],
  ];

  return (
    <div className="h-full flex flex-col justify-center px-2 py-2" data-testid="farm-audit-summary">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {metrics.map(([label, value]) => (
          <button
            key={label}
            type="button"
            className="text-left rounded-md border px-2.5 py-2"
            style={{ borderColor: "var(--c-border)", background: "var(--c-surface)" }}
            onClick={() => onDrillDown?.({ label, value: Number(value || 0) })}
          >
            <div className="text-[11px]" style={{ color: "var(--c-text-muted)" }}>{label}</div>
            <div className="kpi-value mt-0.5">{Number(value || 0).toLocaleString()}</div>
          </button>
        ))}
      </div>

      <div className="flex items-center gap-4 mt-2 px-1 text-[10.5px]" style={{ color: "var(--c-text-muted)" }}>
        {secondary.map(([label, value]) => (
          <span key={label}>
            {label}: <span className="numeric">{Number(value || 0).toLocaleString()}</span>
          </span>
        ))}
        {data?.categories_are_non_exclusive && (
          <span>Certification categories are non-exclusive</span>
        )}
      </div>
    </div>
  );
}

function _cleanRows(rows) {
  // Group rows whose label is blank/null/'-' under "Unassigned"
  const out = [];
  let unassignedTotal = 0;
  let unassignedCount = 0;
  for (const r of rows || []) {
    const lbl = r.label;
    const isBlank = lbl == null || String(lbl).trim() === "" || lbl === "—" || String(lbl).trim() === "-";
    if (isBlank) {
      unassignedTotal += Number(r.value || 0);
      unassignedCount += 1;
    } else {
      out.push({ ...r, label: String(lbl) });
    }
  }
  if (unassignedCount > 0) out.push({ label: "Unassigned", value: unassignedTotal });
  return out;
}

function TooltipBox({ active, payload, label, fmt, total }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border rounded shadow-sm px-2.5 py-1.5 text-[11.5px]" style={{ borderColor: "var(--c-border)" }}>
      <div className="font-medium text-gray-700">{label ?? payload[0]?.name}</div>
      {payload.map((p, i) => {
        const v = Number(p.value || 0);
        const pct = total ? ((v / total) * 100).toFixed(1) + "%" : null;
        return (
          <div key={i} className="flex items-center gap-1.5 numeric">
            <span className="inline-block w-2 h-2 rounded-sm" style={{ background: p.color || p.payload?.fill }} />
            <span style={{ color: "var(--c-text)" }}>{formatNum(v, fmt)}{pct ? ` · ${pct}` : ""}</span>
          </div>
        );
      })}
    </div>
  );
}

function BarW({ data, dimension, fmt, horizontal = false, axisLabels, onDrillDown, buyerSales = false }) {
  const rows = _cleanRows(data?.rows);
  if (buyerSales) return <BuyerSalesExplorer rows={rows} capped={(data?.rows?.length || 0) >= 1000} fmt={fmt} onDrillDown={onDrillDown} />;
  if (!rows.length) return <EmptyState />;

  if (horizontal) {
    const maxLabelLength = Math.max(
      ...rows.map((r) => String(r.label ?? "").length),
      1
    );
    const labelWidth = Math.min(320, Math.max(160, maxLabelLength * 6.5));
    const chartHeight = Math.max(300, rows.length * 32 + 40);

    return (
      <div style={{ width: "100%", height: chartHeight }}>
        {(axisLabels?.x || axisLabels?.y) && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              marginBottom: 8,
              fontSize: 11,
              color: "#64748b",
            }}
          >
            <span>{axisLabels?.y ? `Y-axis: ${axisLabels.y}` : ""}</span>
            <span>{axisLabels?.x ? `X-axis: ${axisLabels.x}` : ""}</span>
          </div>
        )}


        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={rows}
            layout="vertical"
            margin={{ top: 6, right: 18, left: 8, bottom: 6 }}
          >
            <CartesianGrid
              strokeDasharray="2 3"
              stroke="#eef2f7"
              horizontal={false}
            />
            <XAxis
              type="number"
              tick={{ fontSize: 10.5, fill: "#6b7280" }}
              tickFormatter={compact}
            />
            <YAxis
              type="category"
              dataKey="label"
              tick={{ fontSize: 10.5, fill: "#374151" }}
              width={labelWidth}
              interval={0}
            />
            <Tooltip
              content={<TooltipBox fmt={fmt} />}
              cursor={{ fill: "rgba(15,98,254,0.06)" }}
            />
            <Bar
              dataKey="value"
              fill={CHART_COLORS[0]}
              radius={[0, 2, 2, 0]}
              cursor={onDrillDown ? "pointer" : "default"}
              onClick={(entry) => {
                const point = entry?.payload || entry;
                if (onDrillDown && point) onDrillDown(point);
              }}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }


  return (
    <div style={{ width: "100%", height: 280, minHeight: 280 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={rows}
          margin={{ top: 6, right: 8, left: 0, bottom: 6 }}
        >
          <CartesianGrid
            strokeDasharray="2 3"
            stroke="#eef2f7"
            vertical={false}
          />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 10.5, fill: "#6b7280" }}
            interval={0}
            angle={rows.length > 6 ? -25 : 0}
            textAnchor={rows.length > 6 ? "end" : "middle"}
            height={rows.length > 6 ? 50 : 22}
          />
          <YAxis
            tick={{ fontSize: 10.5, fill: "#6b7280" }}
            tickFormatter={compact}
            width={42}
          />
          <Tooltip
            content={<TooltipBox fmt={fmt} />}
            cursor={{ fill: "rgba(15,98,254,0.06)" }}
          />
          <Bar
            dataKey="value"
            fill={CHART_COLORS[0]}
            radius={[2, 2, 0, 0]}
            cursor={onDrillDown ? "pointer" : "default"}
            onClick={(entry) => {
              const point = entry?.payload || entry;
              if (onDrillDown && point) onDrillDown(point);
            }}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}


function TreemapW({
  data,
  fmt,
  onDrillDown,
  excludeUnassigned = false,
  entityLabel = "Item",
}) {
  const cleaned = _cleanRows(data?.rows);
  if (!cleaned.length) return <EmptyState />;

  const unassigned = cleaned.find(
    (row) => String(row.label || "").trim().toLowerCase() === "unassigned"
  );

  let rows = cleaned.filter((row) => {
    if (!excludeUnassigned) return true;
    return String(row.label || "").trim().toLowerCase() !== "unassigned";
  });

  rows = rows
    .filter((row) => Number(row.value || 0) > 0)
    .sort((a, b) => Number(b.value || 0) - Number(a.value || 0));

  if (!rows.length) {
    return <EmptyState msg={`No mapped ${entityLabel.toLowerCase()} data`} />;
  }

  const total = rows.reduce((sum, row) => sum + Number(row.value || 0), 0);

  const chartRows = rows.map((row, index) => ({
    ...row,
    name: row.label,
    size: Number(row.value || 0),
    _index: index,
  }));

  const TreemapCell = ({
    x,
    y,
    width,
    height,
    index,
  }) => {
    const row = chartRows[index];
    if (!row || width <= 0 || height <= 0) return null;

    const value = Number(row.value || 0);
    const pct = total ? (value / total) * 100 : 0;

    const showName = width >= 72 && height >= 30;
    const showValue = width >= 105 && height >= 54;
    const showPct = width >= 125 && height >= 72;

    const TREEMAP_BLUES = [
      "#315A8C",
      "#3F6F9F",
      "#527FAA",
      "#668FB3",
      "#7A9FBC",
      "#91AFC6",
      "#A8BED0",
      "#BECEDB",
    ];

    const fill =
      TREEMAP_BLUES[
        Math.min(
          TREEMAP_BLUES.length - 1,
          Math.floor((index / Math.max(chartRows.length - 1, 1)) * TREEMAP_BLUES.length)
        )
      ];

    return (
      <g
        onClick={() => {
          if (onDrillDown) onDrillDown(row);
        }}
        style={{ cursor: onDrillDown ? "pointer" : "default" }}
      >
        <rect
          x={x + 1}
          y={y + 1}
          width={Math.max(width - 2, 0)}
          height={Math.max(height - 2, 0)}
          rx={4}
          ry={4}
          fill={fill}
          stroke="#ffffff"
          strokeWidth={2}
        />

        {showName && (
          <text
            x={x + 9}
            y={y + 19}
            fill="#ffffff"
            fontSize={11}
            fontWeight={600}
          >
            {String(row.label).length > 30
              ? `${String(row.label).slice(0, 28)}…`
              : row.label}
          </text>
        )}

        {showValue && (
          <text
            x={x + 9}
            y={y + 36}
            fill="rgba(255,255,255,0.92)"
            fontSize={10.5}
          >
            {compact(value)}
          </text>
        )}

        {showPct && (
          <text
            x={x + 9}
            y={y + 51}
            fill="rgba(255,255,255,0.82)"
            fontSize={9.5}
          >
            {pct.toFixed(1)}%
          </text>
        )}
      </g>
    );
  };

  return (
    <div style={{ width: "100%" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 14,
          marginBottom: 10,
          padding: "9px 11px",
          border: "1px solid #dbe4ee",
          borderRadius: 7,
          background: "#f8fafc",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 10.5,
              color: "#64748b",
              marginBottom: 2,
            }}
          >
            Mapped {entityLabel}s
          </div>
          <div
            style={{
              fontSize: 18,
              lineHeight: 1.1,
              fontWeight: 650,
              color: "#163A63",
            }}
          >
            {chartRows.length}
          </div>
        </div>

        <div
          style={{
            fontSize: 10.5,
            color: "#64748b",
            textAlign: "right",
            maxWidth: 260,
          }}
        >
          Larger tiles represent higher contribution.
          Hover for the full client name and value.
        </div>
      </div>

      {excludeUnassigned && unassigned && Number(unassigned.value || 0) > 0 && (
        <div
          style={{
            marginBottom: 10,
            padding: "9px 11px",
            border: "1px solid #dbe4f0",
            borderRadius: 7,
            background: "#fbfcfe",
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            alignItems: "center",
            fontSize: 11.5,
          }}
        >
          <span style={{ color: "#475569" }}>
            Unassigned {entityLabel} mapping
          </span>
          <strong style={{ color: "#0f172a" }}>
            {formatNum(Number(unassigned.value || 0), fmt)}
          </strong>
        </div>
      )}

      <div style={{ width: "100%", height: 390, minHeight: 390 }}>
        <ResponsiveContainer width="100%" height="100%">
          <Treemap
            data={chartRows}
            dataKey="size"
            nameKey="name"
            aspectRatio={4 / 3}
            stroke="#ffffff"
            content={<TreemapCell />}
          >
            <Tooltip
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;

                const raw =
                  payload[0]?.payload?.payload ||
                  payload[0]?.payload ||
                  {};

                const label =
                  raw.label ||
                  raw.name ||
                  payload[0]?.name ||
                  entityLabel;

                const value = Number(
                  raw.value ??
                  raw.size ??
                  payload[0]?.value ??
                  0
                );

                const pct = total
                  ? ((value / total) * 100).toFixed(2)
                  : "0.00";

                return (
                  <div
                    className="bg-white border rounded shadow-sm px-3 py-2 text-[11.5px]"
                    style={{ borderColor: "var(--c-border)" }}
                  >
                    <div className="font-medium text-gray-700 mb-1">
                      {label}
                    </div>
                    <div className="numeric" style={{ color: "var(--c-text)" }}>
                      {formatNum(value, fmt)}
                    </div>
                    <div style={{ color: "var(--c-text-muted)" }}>
                      {pct}% of mapped contribution
                    </div>
                  </div>
                );
              }}
            />
          </Treemap>
        </ResponsiveContainer>
      </div>

      <div
        style={{
          marginTop: 7,
          fontSize: 10.5,
          color: "var(--c-text-muted)",
        }}
      >
        Click any tile to open detailed rows.
      </div>
    </div>
  );
}


function ContributionListW({
  data,
  fmt,
  onDrillDown,
  excludeUnassigned = false,
  entityLabel = "Client",
  coverage = null,
}) {
  const cleaned = _cleanRows(data?.rows);
  if (!cleaned.length) return <EmptyState />;

  const unassigned = cleaned.find(
    (row) =>
      String(row.label || "").trim().toLowerCase() === "unassigned"
  );

  let rows = cleaned.filter((row) => {
    if (!excludeUnassigned) return true;
    return String(row.label || "").trim().toLowerCase() !== "unassigned";
  });

  rows = rows
    .filter((row) => Number(row.value || 0) > 0)
    .sort((a, b) => Number(b.value || 0) - Number(a.value || 0));

  if (!rows.length) {
    return <EmptyState msg={`No mapped ${entityLabel.toLowerCase()} data`} />;
  }

  const total = rows.reduce(
    (sum, row) => sum + Number(row.value || 0),
    0
  );

  const maxValue = Math.max(
    ...rows.map((row) => Number(row.value || 0)),
    1
  );

  const masterTotal = Number(coverage?.master_total || 0);
  const linkedIds = Number(coverage?.sales_linked_ids || 0);
  const linkedNames = Number(coverage?.sales_linked_names || rows.length);

  const isConsignee = entityLabel === "Consignee";

  const linkedForCoverage =
    isConsignee && linkedIds > 0
      ? linkedIds
      : linkedNames;

  const coverageLabel =
    masterTotal > 0
      ? `${linkedForCoverage} of ${masterTotal}`
      : String(rows.length);

  return (
    <div style={{ width: "100%" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto",
          alignItems: "center",
          gap: 14,
          marginBottom: 10,
          padding: "10px 12px",
          border: "1px solid #dfe6ee",
          borderRadius: 7,
          background: "#fafbfd",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 10.5,
              color: "#64748b",
              marginBottom: 2,
            }}
          >
            {entityLabel}s with Sales
          </div>

          <div
            style={{
              fontSize: 19,
              lineHeight: 1.1,
              fontWeight: 650,
              color: "#173b63",
            }}
          >
            {coverageLabel}
          </div>

          {masterTotal > 0 && (
            <div
              style={{
                marginTop: 3,
                fontSize: 10,
                color: "#94a3b8",
                lineHeight: 1.35,
              }}
            >
              {masterTotal - linkedForCoverage} master {entityLabel.toLowerCase()}s not linked to current Sales
              {isConsignee && linkedNames > 0 && linkedIds > linkedNames && (
                <>
                  <br />
                  {linkedNames} unique consignee names represented across {linkedIds} linked IDs
                </>
              )}
            </div>
          )}
        </div>

        <div
          style={{
            fontSize: 10.5,
            lineHeight: 1.4,
            color: "#64748b",
            textAlign: "right",
          }}
        >
          Ranked by contribution
          <br />
          Click a client for detailed rows
        </div>
      </div>

      {excludeUnassigned &&
        unassigned &&
        Number(unassigned.value || 0) > 0 && (
          <div
            style={{
              marginBottom: 10,
              padding: "8px 11px",
              border: "1px solid #e2e8f0",
              borderRadius: 7,
              background: "#fcfcfd",
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              alignItems: "center",
              fontSize: 11.5,
            }}
          >
            <span style={{ color: "#64748b" }}>
              Unassigned {entityLabel} mapping
            </span>

            <strong style={{ color: "#334155" }}>
              {formatNum(Number(unassigned.value || 0), fmt)}
            </strong>
          </div>
        )}

      <div
        style={{
          border: "1px solid #e3e8ef",
          borderRadius: 7,
          overflow: "hidden",
          background: "#ffffff",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "42px minmax(240px, 1fr) 125px 80px 150px",
            gap: 10,
            alignItems: "center",
            padding: "8px 12px",
            background: "#f4f7fa",
            borderBottom: "1px solid #dfe6ee",
            fontSize: 10.5,
            fontWeight: 600,
            color: "#526173",
          }}
        >
          <div>Rank</div>
          <div>{entityLabel}</div>
          <div style={{ textAlign: "right" }}>Sales Value</div>
          <div style={{ textAlign: "right" }}>Share</div>
          <div>Contribution</div>
        </div>

        <div
          style={{
            height: 520,
            minHeight: 520,
            maxHeight: 520,
            overflowY: "auto",
          }}
        >
          {rows.map((row, index) => {
            const value = Number(row.value || 0);
            const pct = total ? (value / total) * 100 : 0;
            const relative = (value / maxValue) * 100;

            return (
              <button
                key={`${row.label}-${index}`}
                type="button"
                onClick={() => {
                  if (onDrillDown) onDrillDown(row);
                }}
                style={{
                  width: "100%",
                  border: 0,
                  borderBottom:
                    index === rows.length - 1
                      ? "none"
                      : "1px solid #edf1f5",
                  background: index % 2 === 0 ? "#ffffff" : "#fbfcfd",
                  display: "grid",
                  gridTemplateColumns:
                    "42px minmax(240px, 1fr) 125px 80px 150px",
                  gap: 10,
                  alignItems: "center",
                  padding: "9px 12px",
                  textAlign: "left",
                  cursor: onDrillDown ? "pointer" : "default",
                }}
                title={`Open ${row.label} details`}
              >
                <div
                  style={{
                    fontSize: 10.5,
                    color: "#94a3b8",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {index + 1}
                </div>

                <div
                  style={{
                    fontSize: 11.5,
                    color: "#334155",
                    fontWeight: index < 5 ? 600 : 500,
                    whiteSpace: "normal",
                    overflowWrap: "anywhere",
                    lineHeight: 1.3,
                  }}
                >
                  {row.label}
                </div>

                <div
                  style={{
                    textAlign: "right",
                    fontSize: 11.5,
                    color: "#1f2937",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {formatNum(value, fmt)}
                </div>

                <div
                  style={{
                    textAlign: "right",
                    fontSize: 11,
                    color: "#64748b",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {pct.toFixed(2)}%
                </div>

                <div
                  style={{
                    height: 7,
                    background: "#eef3f7",
                    borderRadius: 999,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${Math.max(relative, 1.5)}%`,
                      height: "100%",
                      borderRadius: 999,
                      background:
                        index === 0
                          ? "#557da8"
                          : index < 5
                            ? "#7f9fbe"
                            : "#abc0d3",
                    }}
                  />
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function LineW({ data, fmt, onDrillDown }) {
  if (!data?.rows?.length) return <EmptyState />;
  // sort by date label if it looks like ISO
  const rows = [...data.rows].sort((a, b) => String(a.label).localeCompare(String(b.label)));
  const showXTicks = rows.map((r) => String(r.label).slice(0, 10));
  const formatted = rows.map((r, i) => ({ ...r, label: showXTicks[i] }));
  return (
    <div style={{ width: "100%", height: 280, minHeight: 280 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={formatted}
          margin={{ top: 6, right: 8, left: 0, bottom: 6 }}
          onClick={(state) => {
            const point = state?.activePayload?.[0]?.payload;
            if (onDrillDown && point) onDrillDown(point);
          }}
          style={{ cursor: onDrillDown ? "pointer" : "default" }}
        >
          <CartesianGrid strokeDasharray="2 3" stroke="#eef2f7" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#6b7280" }} minTickGap={20} />
          <YAxis tick={{ fontSize: 10.5, fill: "#6b7280" }} tickFormatter={compact} width={42} />
          <Tooltip content={<TooltipBox fmt={fmt} />} />
          <Line type="monotone" dataKey="value" stroke={CHART_COLORS[0]} strokeWidth={1.8} dot={{ r: 2 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function PieW({ data, fmt, donut = false, onDrillDown }) {
  let rows = _cleanRows(data?.rows);
  if (!rows.length) return <EmptyState />;
  // Top-5 + Others
  if (rows.length > 6) {
    const sorted = [...rows].sort((a, b) => Number(b.value || 0) - Number(a.value || 0));
    const top = sorted.slice(0, 5);
    const rest = sorted.slice(5);
    const othersTotal = rest.reduce((s, r) => s + Number(r.value || 0), 0);
    rows = [...top, { label: "Others", value: othersTotal, _others: rest.length }];
  }
  const total = rows.reduce((s, r) => s + Number(r.value || 0), 0);
  return (
    <div style={{ width: "100%", height: donut ? 280 : 330, minHeight: donut ? 280 : 330 }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
        <Pie
          data={rows}
          dataKey="value"
          nameKey="label"
          innerRadius={donut ? "58%" : 0}
          outerRadius={donut ? "78%" : "68%"}
          stroke="#fff"
          strokeWidth={1.5}
          labelLine={!donut}
          label={
            donut
              ? ({ percent }) =>
                  percent >= 0.07 ? `${(percent * 100).toFixed(0)}%` : ""
              : ({ name, percent }) =>
                  percent >= 0.04
                    ? `${name} · ${(percent * 100).toFixed(1)}%`
                    : ""
          }
          cursor={onDrillDown ? "pointer" : "default"}
          onClick={(entry) => {
            if (onDrillDown && entry) onDrillDown(entry);
          }}
        >
          {rows.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
        </Pie>
        <Tooltip content={<TooltipBox fmt={fmt} total={total} />} />
        <Legend verticalAlign="bottom" iconSize={8} wrapperStyle={{ fontSize: 10.5, lineHeight: "14px" }}
                formatter={(label, _entry, idx) => {
                  const r = rows[idx];
                  if (!r) return label;
                  const pct = total ? ((Number(r.value || 0) / total) * 100).toFixed(0) + "%" : "";
                  return `${label} · ${compact(r.value)} (${pct})`;
                }} />
        {donut && (
          <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 13, fontWeight: 600, fill: "#111827" }}>
            {compact(total)}
          </text>
        )}
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

function TableW({ data, onTableData, widget, onProcessStage }) {
  const [sortState, setSortState] = useState({
    columnIndex: null,
    direction: "asc",
  });
  const [tableExpanded, setTableExpanded] = useState(false);

  const columns = useMemo(() => data?.columns || [], [data?.columns]);
  const sourceRows = useMemo(() => data?.rows || [], [data?.rows]);

  useEffect(() => {
    if (typeof onTableData === "function") {
      onTableData({ columns, rows: sourceRows });
    }
  }, [columns, sourceRows, onTableData]);

  if (!columns.length) {
    return <EmptyState msg="No columns selected" />;
  }

  if (!sourceRows.length) return <EmptyState />;

  const rows = [...sourceRows];

  if (sortState.columnIndex !== null) {
    const index = sortState.columnIndex;
    const direction = sortState.direction === "asc" ? 1 : -1;

    rows.sort((a, b) => {
      const left = a[index];
      const right = b[index];

      if (left === right) return 0;
      if (left === null || left === undefined) return 1;
      if (right === null || right === undefined) return -1;

      const leftNumber = Number(left);
      const rightNumber = Number(right);

      if (
        left !== "" &&
        right !== "" &&
        Number.isFinite(leftNumber) &&
        Number.isFinite(rightNumber)
      ) {
        return (leftNumber - rightNumber) * direction;
      }

      return String(left).localeCompare(
        String(right),
        undefined,
        { numeric: true, sensitivity: "base" }
      ) * direction;
    });
  }

  const displayedRows = tableExpanded ? rows : rows.slice(0, 5);
  const canExpandTable = rows.length > 5;

  const changeSort = (columnIndex) => {
    setSortState((current) => {
      if (current.columnIndex === columnIndex) {
        return {
          columnIndex,
          direction: current.direction === "asc" ? "desc" : "asc",
        };
      }

      return {
        columnIndex,
        direction: "asc",
      };
    });
  };

  return (
    <div className="overflow-auto h-full">
      <table className="tbl">
        <thead>
          <tr>
            {columns.map((column, index) => {
              const active = sortState.columnIndex === index;

              return (
                <th key={column}>
                  <button
                    type="button"
                    onClick={() => changeSort(index)}
                    className="w-full flex items-center justify-between gap-2 text-left"
                    title={`Sort by ${column}`}
                  >
                    <span>{column}</span>
                    <span className="text-[10px]">
                      {active
                        ? sortState.direction === "asc"
                          ? "▲"
                          : "▼"
                        : "↕"}
                    </span>
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {displayedRows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((value, columnIndex) => {
                const isNumber = typeof value === "number";

                return (
                  <td
                    key={columnIndex}
                    className={isNumber ? "num" : ""}
                  >
                    {isNumber
                      ? formatNum(value)
                      : value === null
                        ? "—"
                        : String(value)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {canExpandTable && (
        <div className="flex items-center justify-center py-3">
          <button
            type="button"
            onClick={() => setTableExpanded((current) => !current)}
            className="rounded-lg border px-4 py-2 text-xs font-semibold transition hover:bg-slate-50"
            style={{
              borderColor: "var(--c-border)",
              color: "var(--c-text)",
              background: "#fff",
            }}
          >
            {tableExpanded
              ? "Collapse"
              : `Expand · Show all ${rows.length.toLocaleString()} rows`}
          </button>
        </div>
      )}
    </div>
  );
}

// Module-level cache: key -> { data, ts }. Survives layout shuffles/component remounts within a session.
const WIDGET_CACHE = new Map();
const WIDGET_TTL_MS = 60_000;
const EMPTY_DASHBOARD_FILTERS = [];

let SALES_CUSTOMER_COVERAGE_CACHE = null;
let SALES_CUSTOMER_COVERAGE_TS = 0;
const SALES_CUSTOMER_COVERAGE_TTL_MS = 60_000;

function widgetCacheKey(widget, filters) {
  const w = widget || {};
  return JSON.stringify({
    id: w.id, type: w.type, ds: w.dataset_id, m: w.measure, a: w.aggregation,
    d: w.dimension, g: w.group_by, l: w.limit, f: w.filters || [], df: filters || [],
    s: w.sort || null,
    displayDimension: w.display_dimension || null,
    buyerSalesFetchLimit: w.orientation === "buyer-sales" ? 1000 : null,
    sc: w.selected_columns === undefined ? null : w.selected_columns,
  });
}

export default function WidgetRenderer({
  widget,
  dashboardFilters = EMPTY_DASHBOARD_FILTERS,
  onRefreshKey = 0,
  onDrillDown,
  onTableData,
  onProcessStage,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [customerCoverage, setCustomerCoverage] = useState(null);
  const abortRef = useRef(null);

  const load = useCallback(async ({ force = false } = {}) => {
    // Direct SQL widgets can provide their own data.
    // Normalize it to the standard widget-data shape expected by charts.
    if (Array.isArray(widget?.data) && widget?.type === "table") {
      const columns = Object.keys(widget.data[0] || {});
      const rows = widget.data.map((row) =>
        columns.map((column) => row?.[column])
      );

      setData({ rows, columns });
      setErr("");
      setLoading(false);
      return;
    }

    if (Array.isArray(widget?.data)) {
      const rows = widget.data
        .map((row) => {
          const rawValue = row?.value ?? row?.[widget.measure];
          const numericValue = Number(rawValue);

          return {
            ...row,
            label: String(row?.label ?? row?.[widget.dimension] ?? "Unknown"),
            value: Number.isFinite(numericValue) ? numericValue : 0,
          };
        })
        .filter((row) => row.label.trim() !== "");

      setData({
        rows,
        columns: Object.keys(widget.data[0] || {}),
      });
      setErr("");
      setLoading(false);
      return;
    }
    // Cancel any in-flight request for this widget
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch (_) { /* noop */ }
    }
    setErr("");
    if (widget.type === "text") {
      setData({ content: widget.text_content || "" }); setLoading(false); return;
    }
    if (!widget.dataset_id) {
      setData(null); setLoading(false); setErr("No dataset configured");
      return;
    }

    const key = widgetCacheKey(widget, dashboardFilters);
    if (!force) {
      const hit = WIDGET_CACHE.get(key);
      if (hit && (Date.now() - hit.ts) < WIDGET_TTL_MS) {
        setData(hit.data); setLoading(false); return;
      }
    }

    setLoading(true);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const queryWidget = widget.orientation === "buyer-sales"
        ? { ...widget, limit: 1000 }
        :
        widget?.type === "treemap" ||
        widget?.type === "contribution-list"
          ? { ...widget, type: "bar" }
          : widget;

      const r = await api.post(
        "/widget/data",
        { widget: queryWidget, filters: dashboardFilters },
        { signal: ctrl.signal, timeout: 35000 }
      );
      WIDGET_CACHE.set(key, { data: r.data, ts: Date.now() });
      setData(r.data);
    } catch (e) {
      if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return; // superseded
      const msg = e?.response?.data?.detail
        || (e?.code === "ECONNABORTED" ? "Query took too long — try adding a filter or smaller limit." : e.message);
      setErr(String(msg));
    } finally {
      if (abortRef.current === ctrl) {
        abortRef.current = null;
        setLoading(false);
      }
    }
  }, [widget, dashboardFilters]);

  useEffect(() => { load(); return () => { if (abortRef.current) { try { abortRef.current.abort(); } catch (_) { /* noop */ } } }; }, [load, onRefreshKey]);

  useEffect(() => {
    const isCustomerContribution =
      widget?.type === "contribution-list" &&
      (widget?.entity_label === "Buyer" || widget?.entity_label === "Consignee");

    if (!isCustomerContribution) {
      setCustomerCoverage(null);
      return;
    }

    let cancelled = false;

    const loadCoverage = async () => {
      try {
        const now = Date.now();

        let coverageData = SALES_CUSTOMER_COVERAGE_CACHE;

        if (
          !coverageData ||
          now - SALES_CUSTOMER_COVERAGE_TS > SALES_CUSTOMER_COVERAGE_TTL_MS
        ) {
          const r = await api.get("/ceo/sales-customer-coverage");
          coverageData = r.data;

          SALES_CUSTOMER_COVERAGE_CACHE = coverageData;
          SALES_CUSTOMER_COVERAGE_TS = Date.now();
        }

        if (cancelled) return;

        setCustomerCoverage(
          widget?.entity_label === "Buyer"
            ? coverageData?.buyers || null
            : coverageData?.consignees || null
        );
      } catch (e) {
        if (!cancelled) {
          // Fail soft here: the contribution widget itself remains usable.
          // It simply falls back to the linked row count.
          setCustomerCoverage(null);
        }
      }
    };

    loadCoverage();

    return () => {
      cancelled = true;
    };
  }, [widget?.type, widget?.entity_label, onRefreshKey]);

  if (widget.type === "text") {
    return (
      <div className="p-3 text-[13px] whitespace-pre-wrap" data-testid={`widget-text-${widget.id}`}>
        {widget.text_content || <span style={{ color: "var(--c-text-muted)" }}>Empty text widget</span>}
      </div>
    );
  }

  if (loading) return <LoadingState />;
  if (err) return <ErrorState msg={String(err)} onRetry={() => load({ force: true })} />;

  const fmt = widget.number_format || {};
  const openDrillDown = (point = {}) => {
    if (!onDrillDown) return;
    onDrillDown({
      widget,
      point,
      data,
    });
  };

  switch (widget.type) {
    case "ceo-farm-audit-summary": return <FarmAuditSummary data={data} onDrillDown={openDrillDown} />;
    case "kpi":
    case "number":
      return <Kpi data={data} fmt={fmt} title={widget.title} onDrillDown={openDrillDown} />;
    case "bar": return <BarW data={data} dimension={widget.dimension} fmt={fmt} horizontal={widget.orientation === "horizontal"} buyerSales={widget.orientation === "buyer-sales"} axisLabels={widget.axis_labels} onDrillDown={openDrillDown} />;
    case "treemap": return <TreemapW data={data} fmt={fmt} excludeUnassigned={widget.exclude_unassigned === true} entityLabel={widget.entity_label || "Item"} onDrillDown={openDrillDown} />;
    case "contribution-list": return <ContributionListW data={data} fmt={fmt} excludeUnassigned={widget.exclude_unassigned === true} entityLabel={widget.entity_label || "Client"} coverage={customerCoverage} onDrillDown={openDrillDown} />;
    case "line": return <LineW data={data} fmt={fmt} onDrillDown={openDrillDown} />;
    case "pie": return <PieW data={data} fmt={fmt} donut={false} onDrillDown={openDrillDown} />;
    case "donut": return <PieW data={data} fmt={fmt} donut={true} onDrillDown={openDrillDown} />;
    case "table":
      return (
        <TableW
          data={data}
          onTableData={onTableData}
          widget={widget}
          onProcessStage={onProcessStage}
        />
      );
    default: return <EmptyState msg={`Unsupported widget: ${widget.type}`} />;
  }
}
